import type { Emitter } from "@core/events/emitter";

/**
 * `IEditorUIModel` — web port of Unity's `Models/IEditorUIModel.cs`, implemented
 * on desktop by `CampfireScenes/Assets/Campfire/Runtime/ViewModels/EditorViewModel.cs`.
 *
 * Owns whether the editor's two LEFT-REGION panels are expanded: the scenes
 * list and the hierarchy. The header's "Toggle Scenes" / "Toggle Hierarchy"
 * buttons drive it, and `ProjectView` binds the resulting slide.
 *
 * The right column (inspector / comments) is NOT here. Unity keeps it on a
 * separate `IRightPanelUIModel`, where inspector, comments, and add-action are
 * one mutually-exclusive slot behind an auto-open-suppression latch — a
 * different problem from these two independent booleans. Its port is
 * `IRightPanelUIModel.ts` beside this file; `useEditorPanels` drives the two
 * together for Show UI / Hide UI. Don't grow a third flag onto this interface.
 *
 * **Why this is an IOC model and not a module-singleton composable.** Two
 * reasons, both load-bearing:
 *   1. Unity reaches this state from outside the header — `AIOrchestratorModel`
 *      calls `HideAllExpandableUI()` / `ShowAllExpandableUI()` when Spark enters
 *      and leaves play mode, and the entity context menu has Show UI / Hide UI
 *      entries. `src/ai/*` and `src/models/*` may depend on models but never on
 *      `src/viewmodels/*`, so state parked in a composable could not be driven
 *      from those callers without a layering violation.
 *   2. `ProjectView` mounts the left region with `v-if="!workflow.isRunning"`,
 *      which DESTROYS the subtree on every play-mode round trip. Component-local
 *      state would silently reset there while the `v-show`-hidden right region
 *      kept its own, so the two toggles would drift apart.
 *
 * **Deliberate deviations from `EditorViewModel`, do not "fix" back:**
 *   - Unity gives `IsScenesListVisible` a **private** setter while binding it to
 *     a two-way `Toggle` in `HeaderView.prefab`, so the desktop scenes toggle
 *     most likely cannot actually write the flag (only the in-class
 *     `Show/HideAllExpandableUI` can reach it). `IsHierarchyVisible` kept its
 *     public setter. That asymmetry reads as an oversight from
 *     `33395d8dd "hide ui from learner (#3367)"`, which narrowed the interface
 *     to `{ get; }` for both — so the web exposes symmetric setters/togglers
 *     rather than reproducing a half-working control.
 *   - **Not persisted.** Unity backs these with plain `MonoBehaviour` fields —
 *     no `PlayerPrefs`, no cloud field — so visibility resets to all-expanded on
 *     every editor entry. The web matches that. Persisting would be its own
 *     separate deviation, not parity.
 *   - `isSingleUser` gating is NOT implemented: Unity's getters return false for
 *     single-user accounts regardless of the stored flag, and the header buttons
 *     themselves are hidden by a `BoolInversionAdapter` on the same gate. The
 *     web port has no single-user account model yet (tracked in the plan as an
 *     unported header gate), so there is nothing to read. When it lands, gate it
 *     HERE and hide the header buttons too — porting only one half reproduces
 *     the dead-button symptom this feature exists to remove.
 */
export interface IEditorUIModel {
  /** Fires whenever either flag changes. Payload is the new pair. */
  readonly onChanged: Emitter<EditorUIVisibility>;

  /** Unity `IsScenesListVisible`. */
  readonly isScenesListVisible: boolean;

  /** Unity `IsHierarchyVisible`. */
  readonly isHierarchyVisible: boolean;

  /** Unity `IsAllUIHidden` — derived, `!scenes && !hierarchy`. */
  readonly isAllUIHidden: boolean;

  setScenesListVisible(visible: boolean): void;
  setHierarchyVisible(visible: boolean): void;

  toggleScenesList(): void;
  toggleHierarchy(): void;

  /** Unity `ShowAllExpandableUI()` — both flags true. */
  showAllExpandableUI(): void;

  /** Unity `HideAllExpandableUI()` — both flags false. */
  hideAllExpandableUI(): void;
}

/** The pair carried by {@link IEditorUIModel.onChanged}. */
export interface EditorUIVisibility {
  readonly isScenesListVisible: boolean;
  readonly isHierarchyVisible: boolean;
}

export const EDITOR_UI_MODEL = Symbol.for("Campfire.EditorUIModel");
